
import { useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface AdPlacementProps {
  adIndex: number;
  onClose?: () => void;
}

// Mock ad data - in a real app, this would come from an ad service
const mockAds = [
  {
    id: 'ad-1',
    title: 'Premium Farm Equipment',
    description: 'Upgrade your farming with state-of-the-art tractors and tools. Get 20% off your first purchase!',
    image: 'https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=600&h=300&fit=crop',
    ctaText: 'Shop Now',
    ctaUrl: '#',
    advertiser: 'AgriTech Solutions',
    category: 'Equipment'
  },
  {
    id: 'ad-2',
    title: 'Organic Seeds & Fertilizers',
    description: 'Boost your harvest with premium organic seeds and natural fertilizers. Free shipping on orders over $100!',
    image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&h=300&fit=crop',
    ctaText: 'Order Today',
    ctaUrl: '#',
    advertiser: 'Green Growth Co.',
    category: 'Seeds & Supplies'
  },
  {
    id: 'ad-3',
    title: 'Farm Insurance Plans',
    description: 'Protect your crops and livestock with comprehensive farm insurance. Get a free quote in minutes!',
    image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=300&fit=crop',
    ctaText: 'Get Quote',
    ctaUrl: '#',
    advertiser: 'FarmSecure Insurance',
    category: 'Insurance'
  },
  {
    id: 'ad-4',
    title: 'Smart Irrigation Systems',
    description: 'Save water and increase yields with automated irrigation. Easy installation and 24/7 monitoring.',
    image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=600&h=300&fit=crop',
    ctaText: 'Learn More',
    ctaUrl: '#',
    advertiser: 'SmartWater Tech',
    category: 'Technology'
  }
];

export const AdPlacement = ({ adIndex, onClose }: AdPlacementProps) => {
  const [isVisible, setIsVisible] = useState(true);
  
  // Cycle through ads based on the index
  const ad = mockAds[adIndex % mockAds.length];

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const handleClick = () => {
    // In a real app, this would track the click for analytics
    console.log('Ad clicked:', ad.id);
    window.open(ad.ctaUrl, '_blank');
  };

  if (!isVisible) return null;

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl overflow-hidden shadow-sm">
      {/* Ad Header */}
      <div className="flex items-center justify-between p-3 bg-blue-100 border-b border-blue-200">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="bg-blue-200 text-blue-800 text-xs">
            Sponsored
          </Badge>
          <span className="text-sm text-blue-700 font-medium">{ad.advertiser}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClose}
          className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-200"
        >
          <X size={14} />
        </Button>
      </div>

      {/* Ad Content */}
      <div className="p-4">
        <div className="flex gap-4">
          {/* Ad Image */}
          <div className="flex-shrink-0">
            <img
              src={ad.image}
              alt={ad.title}
              className="w-24 h-24 rounded-lg object-cover border border-gray-200"
            />
          </div>

          {/* Ad Text */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-lg mb-1 line-clamp-2">
              {ad.title}
            </h3>
            <p className="text-gray-600 text-sm mb-3 line-clamp-2">
              {ad.description}
            </p>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {ad.category}
              </Badge>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="mt-4 pt-3 border-t border-blue-100">
          <Button
            onClick={handleClick}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            <ExternalLink size={16} className="mr-2" />
            {ad.ctaText}
          </Button>
        </div>
      </div>
    </div>
  );
};
